import React from 'react';
import { FiBox } from 'react-icons/fi';
import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  Box,
  Sphere,
  Cylinder,
  Cone,
  Plane,
  Html,
  Text as DreiText,
} from '@react-three/drei';
import * as THREE from 'three';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Helper components for cleaner 3D scenes
const SceneWrapper = ({ children, cameraPosition = [6, 6, 6] }) => (
  <div
    style={{
      height: '400px',
      background: 'var(--liwaza-surface-container-lowest)',
      borderRadius: '12px',
      border: '1px solid var(--liwaza-outline-variant)',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
    }}
  >
    <Canvas camera={{ position: cameraPosition, fov: 50 }}>
      <ambientLight intensity={0.8} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} />

      {children}

      <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
    </Canvas>
  </div>
);

const ShapeLabel = ({
  children,
  position,
  color = 'white',
  bgColor = 'rgba(0,0,0,0.6)',
}) => (
  <Html position={position} center>
    <div
      style={{
        background: bgColor,
        color: color,
        padding: '0.3rem 0.7rem',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      }}
    >
      {children}
    </div>
  </Html>
);

// 3D Visual Components
const BasicSolidsVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 12]}>
    <group position={[-4.5, 0.75, 0]}>
      <Box args={[1.5, 1.5, 1.5]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
      <ShapeLabel position={[0, 1.2, 0]} bgColor="rgba(37, 99, 235, 0.9)">
        Cube
      </ShapeLabel>
    </group>

    <group position={[-1.5, 1, 0]}>
      <Box args={[1, 2, 1.5]}>
        <meshStandardMaterial color="#10b981" />
      </Box>
      <ShapeLabel position={[0, 1.5, 0]} bgColor="rgba(16, 185, 129, 0.9)">
        Prism
      </ShapeLabel>
    </group>

    <group position={[1.5, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#f59e0b" />
      </Sphere>
      <ShapeLabel position={[0, 1.5, 0]} bgColor="rgba(245, 158, 11, 0.9)">
        Sphere
      </ShapeLabel>
    </group>

    <group position={[4.5, 1, 0]}>
      <Cylinder args={[0.8, 0.8, 2, 32]}>
        <meshStandardMaterial color="#ef4444" />
      </Cylinder>
      <ShapeLabel position={[0, 1.5, 0]} bgColor="rgba(239, 68, 68, 0.9)">
        Cylinder
      </ShapeLabel>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const SurfaceAreaVisual = () => (
  <SceneWrapper cameraPosition={[0, 2, 10]}>
    {/* Cube and its net */}
    <group position={[-3, 0.5, 0]}>
      <ShapeLabel position={[0, 1.5, 0]}>Solid Cube</ShapeLabel>
      <Box args={[1, 1, 1]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
    </group>

    <group position={[1.5, 0, 0]}>
      <ShapeLabel position={[0, 2.5, 0]}>
        Unfolded Net (Surface Area)
      </ShapeLabel>
      {/* Base */}
      <Plane
        args={[1, 1]}
        position={[0, 0.5, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      {/* Sides */}
      <Plane
        args={[1, 1]}
        position={[0, 0.5, 1]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane
        args={[1, 1]}
        position={[1, 0.5, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane
        args={[1, 1]}
        position={[-1, 0.5, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      <Plane
        args={[1, 1]}
        position={[0, 0.5, -1]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
      {/* Top */}
      <Plane
        args={[1, 1]}
        position={[0, 0.5, -2]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <meshStandardMaterial color="#2563eb" side={THREE.DoubleSide} />
      </Plane>
    </group>
    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const SpherePropertiesVisual = () => (
  <SceneWrapper cameraPosition={[0, 2, 8]}>
    <group position={[-2.5, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#2563eb" wireframe={true} />
      </Sphere>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#2563eb" transparent opacity={0.3} />
      </Sphere>
      <ShapeLabel position={[0, 1.5, 0]} bgColor="rgba(37, 99, 235, 0.9)">
        Volume = (4/3)πr³
      </ShapeLabel>
    </group>

    <group position={[2.5, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#10b981" transparent opacity={0.5} />
      </Sphere>
      <Cylinder args={[1.01, 1.01, 0.05, 64]}>
        <meshStandardMaterial
          color="#ef4444"
          emissive="#ef4444"
          emissiveIntensity={0.6}
        />
      </Cylinder>
      <ShapeLabel position={[0, 1.5, 0]} bgColor="rgba(16, 185, 129, 0.9)">
        Surface Area = 4πr²
      </ShapeLabel>
    </group>

    <Plane args={[10, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const VolumeRelationshipsVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 12]}>
    <DreiText
      position={[0, 3, 0]}
      fontSize={0.3}
      color="black"
      textAlign="center"
      anchorX="center"
      maxWidth={5}
    >
      Volume Comparison (side = diameter = height = s)
    </DreiText>

    {/* Cube */}
    <group position={[-3.5, 1, 0]}>
      <Box args={[2, 2, 2]}>
        <meshStandardMaterial color="#2563eb" />
      </Box>
      <ShapeLabel position={[0, 1.5, 0]}>Cube (V = s³)</ShapeLabel>
    </group>

    {/* Cylinder */}
    <group position={[0, 1, 0]}>
      <Cylinder args={[1, 1, 2, 32]}>
        <meshStandardMaterial color="#10b981" />
      </Cylinder>
      <ShapeLabel position={[0, 1.5, 0]}>Cylinder (V ≈ 0.785s³)</ShapeLabel>
    </group>

    {/* Sphere */}
    <group position={[3.5, 1, 0]}>
      <Sphere args={[1, 32, 32]}>
        <meshStandardMaterial color="#f59e0b" />
      </Sphere>
      <ShapeLabel position={[0, 1.5, 0]}>Sphere (V ≈ 0.524s³)</ShapeLabel>
    </group>

    <Plane args={[15, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const CrossSectionsVisual = () => (
  <SceneWrapper cameraPosition={[0, 2.5, 10]}>
    {/* Cylinder */}
    <group position={[-3, 1.25, 0]}>
      <ShapeLabel position={[0, 2.8, 0]}>Cylinder Slices</ShapeLabel>
      <Cylinder args={[1, 1, 2.5, 32]}>
        <meshStandardMaterial color="#2563eb" transparent opacity={0.4} />
      </Cylinder>
      {/* Horizontal Slice */}
      <Cylinder args={[1.01, 1.01, 0.05, 64]} position={[0, 0.5, 0]}>
        <meshStandardMaterial color="#ef4444" />
      </Cylinder>
      <DreiText
        position={[1.2, 0.5, 0]}
        rotation={[0, 0, 0]}
        fontSize={0.2}
        color="black"
      >
        Circle
      </DreiText>
    </group>

    {/* Cone */}
    <group position={[3, 1.25, 0]}>
      <ShapeLabel position={[0, 2.8, 0]}>Cone Slices</ShapeLabel>
      <Cone args={[1, 2.5, 32]}>
        <meshStandardMaterial color="#f59e0b" transparent opacity={0.4} />
      </Cone>
      {/* Horizontal Slice */}
      <Cylinder args={[0.51, 0.51, 0.05, 64]} position={[0, -0.25, 0]}>
        <meshStandardMaterial color="#ef4444" />
      </Cylinder>
      <DreiText
        position={[0.8, -0.25, 0]}
        rotation={[0, 0, 0]}
        fontSize={0.2}
        color="black"
      >
        Circle
      </DreiText>
    </group>

    <Plane args={[12, 8]} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <meshStandardMaterial color="#f0f0f0" />
    </Plane>
  </SceneWrapper>
);

const ThreeDimensionalFiguresLesson = () => {
  const sections = [
    {
      id: 'basic-solids',
      title: 'Basic 3D Shapes',
      content: {
        introduction:
          'Three-dimensional figures have length, width, and height. Understanding their properties is essential for volume and surface area calculations.',

        concepts: [
          {
            title: 'Cube (Hexahedron)',
            explanation:
              'A cube has 6 square faces, 12 edges, and 8 vertices. All edges are equal length.',
            visual: (
              <BasicSolidsVisual>
                <mesh>
                  <boxGeometry args={[2.5, 2.5, 2.5]} />
                  <meshStandardMaterial color="#2563eb" wireframe />
                  <ShapeLabel position={[0, -2, 0]}>Cube</ShapeLabel>
                </mesh>
              </BasicSolidsVisual>
            ),
            example: {
              problem:
                'A cube has a side length of 5 cm. Find its volume and surface area.',
              solution: [
                'Volume of a cube = §s^3§',
                'V = §5^3 = 125§ cm³',
                'Surface area of a cube = §6s^2§',
                'SA = §6 \\times 5^2 = 6 \\times 25 = 150§ cm²',
              ],
            },
          },
          {
            title: 'Rectangular Prism',
            explanation: 'A solid with 6 rectangular faces.',
            visual: (
              <BasicSolidsVisual>
                <mesh>
                  <boxGeometry args={[3, 2, 1.5]} />
                  <meshStandardMaterial color="#10b981" wireframe />
                  <ShapeLabel position={[0, -2, 0]}>
                    Rectangular Prism
                  </ShapeLabel>
                </mesh>
              </BasicSolidsVisual>
            ),
            example: {
              problem:
                'Find the volume of a rectangular prism with dimensions 4x5x6.',
              solution: [
                'Volume = §l \\times w \\times h§',
                'V = §4 \\times 5 \\times 6 = 120§',
                'The volume is 120 cubic units.',
              ],
            },
          },
          {
            title: 'Cylinder',
            explanation: 'A solid with two parallel circular bases.',
            visual: (
              <BasicSolidsVisual>
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                  <cylinderGeometry args={[1.5, 1.5, 3, 32]} />
                  <meshStandardMaterial color="#f59e0b" wireframe />
                  <ShapeLabel position={[0, -2.5, 0]}>Cylinder</ShapeLabel>
                </mesh>
              </BasicSolidsVisual>
            ),
            example: {
              problem:
                'A cylinder has radius 3 and height 10. Find its volume.',
              solution: [
                'Volume = §\\pi r^2 h§',
                'V = §\\pi \\times 3^2 \\times 10 = 90\\pi§',
                'The volume is §90\\pi§ cubic units.',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'cubes-prisms',
      title: 'Cubes & Prisms',
      content: {
        introduction:
          'Cubes and rectangular prisms are polyhedra with flat faces. Their volumes and surface areas follow straightforward formulas.',

        concepts: [
          {
            title: 'Unfolding Solids',
            explanation:
              'Surface area is the total area of all faces of a 3D shape. Imagine "unfolding" the shape into a 2D net.',
            visual: <SurfaceAreaVisual />,
            example: {
              problem: 'Find the surface area of a cube with side length 4.',
              solution: [
                'A cube has 6 identical square faces.',
                'Area of one face = §s^2 = 4^2 = 16§',
                'Total surface area = §6 \\times 16 = 96§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'cylinders',
      title: 'Cylinders',
      content: {
        introduction:
          'Cylinders have two parallel circular bases connected by a curved surface. They are common in real-world applications.',

        concepts: [
          {
            title: 'Cylinder Properties',
            explanation:
              "Volume = πr²h, Surface area = 2πr² + 2πrh. The curved surface can be 'unrolled' into a rectangle.",
            visual: <CrossSectionsVisual />,
            example: {
              problem:
                'A cylinder has radius 3 and height 8. Find its volume and surface area',
              solution: [
                'Volume = πr²h = π(3²)(8) = 72π cubic units',
                'Surface area = 2πr² + 2πrh',
                '= 2π(9) + 2π(3)(8) = 18π + 48π = 66π square units',
                '≈ 207.3 square units',
              ],
            },
          },
          {
            title: 'Sphere Formulas',
            explanation:
              'A sphere is a perfectly round 3D object. Its properties depend only on its radius.',
            visual: <SpherePropertiesVisual />,
            example: {
              problem: 'A sphere has a radius of 3. Find its volume.',
              solution: [
                'Volume of a sphere = §(4/3)\\pi r^3§',
                'V = §(4/3)\\pi (3^3) = (4/3)\\pi (27)§',
                'V = §36\\pi§ cubic units',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'spheres',
      title: 'Spheres',
      content: {
        introduction:
          'Spheres are perfectly round 3D figures where every point on the surface is equidistant from the center.',

        concepts: [
          {
            title: 'Sphere Properties',
            explanation:
              'Volume = (4/3)πr³, Surface area = 4πr². The sphere has maximum volume for a given surface area.',
            visual: <SpherePropertiesVisual />,
            example: {
              problem:
                'A sphere has diameter 12. Find its volume and surface area',
              solution: [
                'Diameter = 12, so radius = 6',
                'Volume = (4/3)πr³ = (4/3)π(6³) = (4/3)π(216) = 288π',
                'Surface area = 4πr² = 4π(6²) = 4π(36) = 144π',
                'Volume ≈ 905 cubic units, Surface area ≈ 452 square units',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'volume-comparison',
      title: 'Volume Relationships',
      content: {
        introduction:
          'Comparing volumes of different shapes reveals interesting mathematical relationships. Understanding these helps with optimization problems.',

        concepts: [
          {
            title: 'Cylinder vs. Cone',
            explanation:
              'A cone with the same base and height as a cylinder has exactly 1/3 the volume.',
            visual: <VolumeRelationshipsVisual />,
            example: {
              problem:
                'A cylinder has a volume of 90. A cone has the same base and height. What is the cone’s volume?',
              solution: [
                'Volume of cone = §(1/3) \\times \\text{Volume of cylinder}§',
                'V = §(1/3) \\times 90 = 30§',
                'The cone’s volume is 30 cubic units.',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'cross-sections',
      title: 'Cross-Sections',
      content: {
        introduction:
          'A cross-section is the 2D shape you get when you slice through a 3D object.',
        concepts: [
          {
            title: 'Slicing 3D Shapes',
            explanation:
              'A cross-section is the 2D shape you get when you slice through a 3D object.',
            visual: <CrossSectionsVisual />,
            example: {
              problem:
                'What shape is the cross-section when a cylinder is sliced horizontally?',
              solution: [
                'Slicing a cylinder parallel to its base always results in a circle.',
                'The circle will have the same radius as the cylinder’s base.',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A cube has a side length of §4§ cm. What is its surface area in cm²?',
      options: ['64', '96', '128', '32', '48'],
      correct: 1,
      explanation:
        'Surface area of a cube = §6s^2 = 6 \\times 4^2 = 6 \\times 16 = 96§ cm²',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A cylinder has a radius of §5§ and a height of §10§. What is its volume?',
      options: ['§50\\pi§', '§100\\pi§', '§250\\pi§', '§500\\pi§', '§125\\pi§'],
      correct: 2,
      explanation:
        'Volume of a cylinder = §\\pi r^2 h = \\pi \\times 5^2 \\times 10 = 250\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'A sphere has a radius of 3. What is its volume?',
      options: ['§12\\pi§', '§18\\pi§', '§27\\pi§', '§36\\pi§', '§54\\pi§'],
      correct: 3,
      explanation:
        'Volume of a sphere = §(4/3)\\pi r^3 = (4/3)\\pi (3^3) = (4/3)\\pi (27) = 36\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A rectangular prism has dimensions 3, 4, and 5. What is the length of its longest diagonal?',
      options: ['§\\sqrt{50}§', '§\\sqrt{34}§', '§\\sqrt{41}§', '12', '60'],
      correct: 0,
      explanation:
        'Longest diagonal = §\\sqrt{l^2 + w^2 + h^2} = \\sqrt{3^2 + 4^2 + 5^2} = \\sqrt{9 + 16 + 25} = \\sqrt{50}§',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A cone has a radius of 6 and a height of 8. What is its volume?',
      options: ['§96\\pi§', '§128\\pi§', '§144\\pi§', '§64\\pi§', '§72\\pi§'],
      correct: 0,
      explanation:
        'Volume of a cone = §(1/3)\\pi r^2 h = (1/3)\\pi (6^2)(8) = (1/3)\\pi(36)(8) = 96\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'If a sphere and a cylinder have the same radius and the cylinder’s height is equal to its diameter, what is the ratio of their volumes?',
      options: ['1:1', '2:3', '3:4', '1:2', '4:3'],
      correct: 1,
      explanation:
        '§V_{sphere} = \\frac{4}{3}\\pi r^3§. §V_{cylinder} = \\pi r^2 h = \\pi r^2 (2r) = 2\\pi r^3§. Ratio = §\\frac{\\frac{4}{3}\\pi r^3}{2\\pi r^3} = \\frac{2}{3}§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'What is the surface area of a cylinder with radius 4 and height 10?',
      options: ['§112\\pi§', '§128\\pi§', '§144\\pi§', '§80\\pi§', '§96\\pi§'],
      correct: 0,
      explanation:
        'Surface area = §2\\pi r^2 + 2\\pi rh = 2\\pi(4^2) + 2\\pi(4)(10) = 32\\pi + 80\\pi = 112\\pi§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A cross-section of a cube can be which of the following shapes?',
      options: [
        'All of the above',
        'Triangle',
        'Square',
        'Hexagon',
        'Rectangle',
      ],
      correct: 0,
      explanation:
        'A cube can be sliced to create a triangle, square, rectangle, pentagon, or hexagon.',
    },
    {
      type: 'timed',
      question: 'What is the volume of a cube with a side length of 6 cm?',
      options: ['216 cm³', '180 cm³', '144 cm³', '108 cm³', '256 cm³'],
      correct: 0,
      explanation: 'Volume of a cube = §s^3 = 6^3 = 216§ cm³.',
    },
    {
      type: 'timed',
      question: 'What is the surface area of a cube with edge length s?',
      options: ['6s²', 's³', '4s²', '3s²', '12s'],
      correct: 0,
      explanation:
        'A cube has 6 faces, each with area §s^2§. Total surface area = §6s^2§',
    },
    {
      type: 'timed',
      question: 'Which solid has the greatest volume for a given surface area?',
      options: ['Sphere', 'Cube', 'Cylinder', 'Cone', 'Rectangular prism'],
      correct: 0,
      explanation:
        'Among all solids with the same surface area, a sphere has the maximum volume (isoperimetric inequality in 3D).',
    },
    {
      type: 'timed',
      question:
        'A cylinder and cone have the same radius and height. The volume ratio cylinder:cone is:',
      options: ['3:1', '2:1', '4:1', '1:1', '6:1'],
      correct: 0,
      explanation:
        'Cylinder volume = §\\pi r^2 h§, Cone volume = §(1/3)\\pi r^2 h§. Ratio = §\\pi r^2 h : (1/3)\\pi r^2 h = 3:1§',
    },
    {
      type: 'timed',
      question: 'How many vertices does a rectangular prism have?',
      options: ['8', '6', '12', '4', '10'],
      correct: 0,
      explanation: 'A rectangular prism (box) has 8 vertices (corners).',
    },
    {
      type: 'timed',
      question: 'How many edges does a cube have?',
      options: ['12', '8', '6', '10', '14'],
      correct: 0,
      explanation:
        'A cube has 12 edges: 4 on top face, 4 on bottom face, 4 connecting them.',
    },
    {
      type: 'timed',
      question:
        'A sphere has a surface area of §100§π cm². What is its radius?',
      options: ['5 cm', '10 cm', '25 cm', '50 cm', '2.5 cm'],
      correct: 0,
      explanation:
        'Surface area = §4\\pi r^2 = 100\\pi§. So §r^2 = 25 \\rightarrow r = 5§ cm.',
    },
    {
      type: 'timed',
      question:
        'A rectangular prism has dimensions 6 cm × 5 cm × 4 cm. What is its total surface area?',
      options: ['148 cm²', '120 cm²', '240 cm²', '180 cm²', '200 cm²'],
      correct: 0,
      explanation:
        'SA = §2(lw + lh + wh) = 2(6 \\times 5 + 6 \\times 4 + 5 \\times 4) = 2(30 + 24 + 20) = 2(74) = 148§ cm².',
    },
    {
      type: 'timed',
      question: 'Which formula gives the volume of a sphere?',
      options: [
        '§(4/3)\\pi r^3§',
        '§4\\pi r^2§',
        '§\\pi r^2 h§',
        '§2\\pi r^2§',
        '§(1/3)\\pi r^2 h§',
      ],
      correct: 0,
      explanation:
        'The volume of a sphere is §(4/3)\\pi r^3§ where r is the radius.',
    },
    {
      type: 'timed',
      question: 'A rectangular prism has dimensions 3×4×5. What is its volume?',
      options: ['60', '47', '12', '20', '72'],
      correct: 0,
      explanation:
        'Volume = §length \\times width \\times height = 3 \\times 4 \\times 5 = 60§',
    },
    {
      type: 'timed',
      question: 'The surface area of a cube with edge length s is:',
      options: ['§6s^2§', '§s^3§', '§4s^2§', '§3s^2§', '§12s§'],
      correct: 0,
      explanation:
        'A cube has 6 faces, each with area §s^2§. Total surface area = §6s^2§',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A cube has a side length of §4§ cm. What is its surface area in cm²?',
      options: ['64', '96', '128', '32', '48'],
      correct: 1,
      explanation:
        'Surface area of a cube = §6s^2 = 6 \\times 4^2 = 6 \\times 16 = 96§ cm²',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A cylinder has a radius of §5§ and a height of §10§. What is its volume?',
      options: ['§50\\pi§', '§100\\pi§', '§250\\pi§', '§500\\pi§', '§125\\pi§'],
      correct: 2,
      explanation:
        'Volume of a cylinder = §\\pi r^2 h = \\pi \\times 5^2 \\times 10 = 250\\pi§',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'A sphere has a radius of 3. What is its volume?',
      options: ['§12\\pi§', '§18\\pi§', '§27\\pi§', '§36\\pi§', '§54\\pi§'],
      correct: 3,
      explanation:
        'Volume of a sphere = §(4/3)\\pi r^3 = (4/3)\\pi (3^3) = (4/3)\\pi (27) = 36\\pi§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'A rectangular prism has dimensions 3, 4, and 5. What is the length of its longest diagonal?',
      options: ['§\\sqrt{50}§', '§\\sqrt{34}§', '§\\sqrt{41}§', '12', '60'],
      correct: 0,
      explanation:
        'Longest diagonal = §\\sqrt{l^2 + w^2 + h^2} = \\sqrt{3^2 + 4^2 + 5^2} = \\sqrt{9 + 16 + 25} = \\sqrt{50}§',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'A cone has a radius of 6 and a height of 8. What is its volume?',
      options: ['§96\\pi§', '§128\\pi§', '§144\\pi§', '§64\\pi§', '§72\\pi§'],
      correct: 0,
      explanation:
        'Volume of a cone = §(1/3)\\pi r^2 h = (1/3)\\pi (6^2)(8) = (1/3)\\pi(36)(8) = 96\\pi§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'If a sphere and a cylinder have the same radius and the cylinder’s height is equal to its diameter, what is the ratio of their volumes?',
      options: ['1:1', '2:3', '3:4', '1:2', '4:3'],
      correct: 1,
      explanation:
        '§V_{sphere} = \\frac{4}{3}\\pi r^3§. §V_{cylinder} = \\pi r^2 h = \\pi r^2 (2r) = 2\\pi r^3§. Ratio = §\\frac{\\frac{4}{3}\\pi r^3}{2\\pi r^3} = \\frac{2}{3}§.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'What is the surface area of a cylinder with radius 4 and height 10?',
      options: ['§112\\pi§', '§128\\pi§', '§144\\pi§', '§80\\pi§', '§96\\pi§'],
      correct: 0,
      explanation:
        'Surface area = §2\\pi r^2 + 2\\pi rh = 2\\pi(4^2) + 2\\pi(4)(10) = 32\\pi + 80\\pi = 112\\pi§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A cross-section of a cube can be which of the following shapes?',
      options: [
        'All of the above',
        'Triangle',
        'Square',
        'Hexagon',
        'Rectangle',
      ],
      correct: 0,
      explanation:
        'A cube can be sliced to create a triangle, square, rectangle, pentagon, or hexagon.',
    },
    {
      type: 'untimed',
      question: 'What is the volume of a cube with a side length of 6 cm?',
      options: ['216 cm³', '180 cm³', '144 cm³', '108 cm³', '256 cm³'],
      correct: 0,
      explanation: 'Volume of a cube = §s^3 = 6^3 = 216§ cm³.',
    },
    {
      type: 'untimed',
      question: 'What is the surface area of a cube with edge length s?',
      options: ['6s²', 's³', '4s²', '3s²', '12s'],
      correct: 0,
      explanation:
        'A cube has 6 faces, each with area §s^2§. Total surface area = §6s^2§',
    },
    {
      type: 'untimed',
      question: 'Which solid has the greatest volume for a given surface area?',
      options: ['Sphere', 'Cube', 'Cylinder', 'Cone', 'Rectangular prism'],
      correct: 0,
      explanation:
        'Among all solids with the same surface area, a sphere has the maximum volume (isoperimetric inequality in 3D).',
    },
    {
      type: 'untimed',
      question:
        'A cylinder and cone have the same radius and height. The volume ratio cylinder:cone is:',
      options: ['3:1', '2:1', '4:1', '1:1', '6:1'],
      correct: 0,
      explanation:
        'Cylinder volume = §\\pi r^2 h§, Cone volume = §(1/3)\\pi r^2 h§. Ratio = §\\pi r^2 h : (1/3)\\pi r^2 h = 3:1§',
    },
    {
      type: 'untimed',
      question: 'How many vertices does a rectangular prism have?',
      options: ['8', '6', '12', '4', '10'],
      correct: 0,
      explanation: 'A rectangular prism (box) has 8 vertices (corners).',
    },
    {
      type: 'untimed',
      question: 'How many edges does a cube have?',
      options: ['12', '8', '6', '10', '14'],
      correct: 0,
      explanation:
        'A cube has 12 edges: 4 on top face, 4 on bottom face, 4 connecting them.',
    },
    {
      type: 'untimed',
      question:
        'A sphere has a surface area of §100§π cm². What is its radius?',
      options: ['5 cm', '10 cm', '25 cm', '50 cm', '2.5 cm'],
      correct: 0,
      explanation:
        'Surface area = §4\\pi r^2 = 100\\pi§. So §r^2 = 25 \\rightarrow r = 5§ cm.',
    },
    {
      type: 'untimed',
      question:
        'A rectangular prism has dimensions 6 cm × 5 cm × 4 cm. What is its total surface area?',
      options: ['148 cm²', '120 cm²', '240 cm²', '180 cm²', '200 cm²'],
      correct: 0,
      explanation:
        'SA = §2(lw + lh + wh) = 2(6 \\times 5 + 6 \\times 4 + 5 \\times 4) = 2(30 + 24 + 20) = 2(74) = 148§ cm².',
    },
    {
      type: 'untimed',
      question: 'Which formula gives the volume of a sphere?',
      options: [
        '§(4/3)\\pi r^3§',
        '§4\\pi r^2§',
        '§\\pi r^2 h§',
        '§2\\pi r^2§',
        '§(1/3)\\pi r^2 h§',
      ],
      correct: 0,
      explanation:
        'The volume of a sphere is §(4/3)\\pi r^3§ where r is the radius.',
    },
    {
      type: 'untimed',
      question: 'A rectangular prism has dimensions 3×4×5. What is its volume?',
      options: ['60', '47', '12', '20', '72'],
      correct: 0,
      explanation:
        'Volume = §length \\times width \\times height = 3 \\times 4 \\times 5 = 60§',
    },
    {
      type: 'untimed',
      question: 'The surface area of a cube with edge length s is:',
      options: ['§6s^2§', '§s^3§', '§4s^2§', '§3s^2§', '§12s§'],
      correct: 0,
      explanation:
        'A cube has 6 faces, each with area §s^2§. Total surface area = §6s^2§',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'A cube has edge length 4. Compare the two quantities.',
      quantityA: 'Volume',
      quantityB: 'Surface area',
      correct: 1,
      explanation:
        'Compare the two quantities directly. Quantity B is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which are three-dimensional solids? Select all that apply.',
      options: ['Cube', 'Cylinder', 'Circle', 'Sphere', 'Triangle'],
      correct: [0, 1, 3],
      explanation:
        'Cubes, cylinders, and spheres are solids; circles and triangles are plane figures.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'A rectangular prism is §2 x 3 x 5§. Enter its volume.',
      correctAnswer: '30',
      acceptedAnswers: ['30'],
      explanation: 'Volume is length times width times height: §2*3*5=30§.',
    },
  ];

  const quickReference = [
    'Cube Volume: §s^3§',
    'Cylinder Volume: §\\pi r^2 h§',
    'Sphere Volume: §(4/3)\\pi r^3§',
    'Cone Volume: §(1/3)\\pi r^2 h§',
    'Cube Surface Area: §6s^2§',
    'Cylinder Surface Area: §2\\pi rh + 2\\pi r^2§',
    'Sphere Surface Area: §4\\pi r^2§',
    'Diagonal of a box: §\\sqrt{l^2+w^2+h^2}§',
  ];

  const getStartedWithProps = {
    title: 'Need help with 3D Figures?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding 3D Figures concepts for the GRE Quantitative section',
    aicontext:
      'Master cubes, cylinders, spheres, cones, and their volume and surface area formulas',
  };

  return (
    <LessonTemplate
      lessonId="three-dimensional-figures"
      lessonTitle="3-Dimensional Figures"
      lessonIcon={<FiBox />}
      lessonDescription="Master cubes, prisms, cylinders, spheres, and volume calculations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="three-dimensional-figures" />
      }
    />
  );
};

export default ThreeDimensionalFiguresLesson;
